/**
 * DSL for configuring potential categorization per client.
 *
 * A ruleset is plain JSON — safe to store in the database and editable
 * through a UI by non-technical users.
 *
 * ─── Available fields ────────────────────────────────────────────────────────
 *
 *   curva          {number}  Curve index from getPotentialTalentArray
 *   nivelPotCurva  {string}  Peak level string on the Jacques curve (e.g. "4B", "3C")
 *   peakLevel      {string}  First character of nivelPotCurva (e.g. "4", "3", "2")
 *
 * ─── Operators ───────────────────────────────────────────────────────────────
 *
 *   ==   equal
 *   !=   not equal
 *   >=   greater than or equal
 *   <=   less than or equal
 *   >    greater than
 *   <    less than
 *
 * ─── Condition types ─────────────────────────────────────────────────────────
 *
 *   Leaf:     { "field": "<field>", "op": "<operator>", "value": <value> }
 *   AND:      { "and": [ <condition>, ... ] }
 *   OR:       { "or":  [ <condition>, ... ] }
 *
 * ─── Ruleset structure ───────────────────────────────────────────────────────
 *
 *   {
 *     "rules": [
 *       { "when": <condition>, "then": "HIGH" | "MEDIUM" | "LOW" },
 *       ...
 *     ],
 *     "default": "HIGH" | "MEDIUM" | "LOW"
 *   }
 *
 *   Rules are evaluated in order. The first rule whose "when" condition
 *   matches wins. If no rule matches, "default" is returned.
 *
 * ─── Example ─────────────────────────────────────────────────────────────────
 *
 *   {
 *     "rules": [
 *       {
 *         "when": { "field": "curva", "op": ">=", "value": 10 },
 *         "then": "HIGH"
 *       },
 *       {
 *         "when": { "or": [
 *           { "field": "peakLevel",     "op": "==", "value": "2"  },
 *           { "field": "nivelPotCurva", "op": "==", "value": "3C" }
 *         ]},
 *         "then": "LOW"
 *       },
 *       {
 *         "when": { "or": [
 *           { "field": "peakLevel",     "op": "==", "value": "3"  },
 *           { "field": "nivelPotCurva", "op": "==", "value": "4C" }
 *         ]},
 *         "then": "MEDIUM"
 *       }
 *     ],
 *     "default": "HIGH"
 *   }
 */

const VALID_RESULTS = new Set(['HIGH', 'MEDIUM', 'LOW']);
const VALID_FIELDS  = new Set(['curva', 'nivelPotCurva', 'peakLevel']);
const VALID_OPS     = new Set(['==', '!=', '>=', '<=', '>', '<']);

function validateCondition(condition, path, errors) {
    if (typeof condition !== 'object' || condition === null || Array.isArray(condition)) {
        errors.push(`${path}: must be an object`);
        return;
    }

    if ('or' in condition) {
        if (!Array.isArray(condition.or) || condition.or.length === 0)
            errors.push(`${path}.or: must be a non-empty array`);
        else
            condition.or.forEach((c, i) => validateCondition(c, `${path}.or[${i}]`, errors));
    } else if ('and' in condition) {
        if (!Array.isArray(condition.and) || condition.and.length === 0)
            errors.push(`${path}.and: must be a non-empty array`);
        else
            condition.and.forEach((c, i) => validateCondition(c, `${path}.and[${i}]`, errors));
    } else if ('field' in condition || 'op' in condition || 'value' in condition) {
        // Leaf condition
        if (!VALID_FIELDS.has(condition.field))
            errors.push(`${path}.field: "${condition.field}" is not valid — use one of: ${[...VALID_FIELDS].join(', ')}`);
        if (!VALID_OPS.has(condition.op))
            errors.push(`${path}.op: "${condition.op}" is not valid — use one of: ${[...VALID_OPS].join(', ')}`);
        if (condition.value === undefined || condition.value === null || (typeof condition.value !== 'string' && typeof condition.value !== 'number'))
            errors.push(`${path}.value: must be a string or number`);
    } else {
        errors.push(`${path}: must specify a branch — use 'or', 'and', or a leaf with 'field', 'op', 'value'`);
    }
}

/**
 * Checks whether a JSON string is a valid potential ruleset.
 * Returns { valid: boolean, errors: string[] } where errors contains
 * human-readable messages suitable for displaying in a UI.
 *
 * @param {string} input - JSON string (e.g. from the database)
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validatePotentialRuleset(input) {
    const errors = [];

    let ruleset;
    try {
        ruleset = JSON.parse(input);
    } catch (e) {
        return { valid: false, errors: [`Invalid JSON: ${e.message}`] };
    }

    if (typeof ruleset !== 'object' || ruleset === null || Array.isArray(ruleset))
        return { valid: false, errors: ['Ruleset must be a JSON object'] };

    if (!VALID_RESULTS.has(ruleset.default))
        errors.push(`"default" must be one of: ${[...VALID_RESULTS].join(', ')}`);

    if (!Array.isArray(ruleset.rules)) {
        errors.push('"rules" must be an array');
    } else {
        ruleset.rules.forEach((rule, i) => {
            if (typeof rule !== 'object' || rule === null) {
                errors.push(`rules[${i}]: must be an object`);
                return;
            }
            if (!rule.when)
                errors.push(`rules[${i}]: missing "when"`);
            else
                validateCondition(rule.when, `rules[${i}].when`, errors);

            if (!VALID_RESULTS.has(rule.then))
                errors.push(`rules[${i}].then: must be one of: ${[...VALID_RESULTS].join(', ')}`);
        });
    }

    return { valid: errors.length === 0, errors };
}

function evaluateCondition(condition, context) {
    if ('or' in condition)  return condition.or.some(c  => evaluateCondition(c, context));
    if ('and' in condition) return condition.and.every(c => evaluateCondition(c, context));

    const val = context[condition.field];
    switch (condition.op) {
        case '==': return val === condition.value;
        case '!=': return val !== condition.value;
        case '>=': return val >= condition.value;
        case '<=': return val <= condition.value;
        case '>':  return val >  condition.value;
        case '<':  return val <  condition.value;
        default:   return false;
    }
}

/**
 * Evaluates a potential ruleset against the given inputs.
 * Rules are evaluated in order; the first matching rule wins.
 * Falls back to ruleset.default if no rule matches.
 *
 * @param {Object} ruleset       - A ruleset object (can be parsed from DB JSON)
 * @param {string} nivelPotCurva - Peak level on the curve (e.g. "4B")
 * @param {number} curva         - Curve index from getPotentialTalentArray
 * @returns {string}
 */
export function evaluatePotentialRuleset(ruleset, nivelPotCurva, curva) {
    const context = {
        curva,
        nivelPotCurva,
        peakLevel: nivelPotCurva.charAt(0),
    };

    for (const rule of ruleset.rules) {
        if (evaluateCondition(rule.when, context)) return rule.then;
    }

    return ruleset.default;
}

/**
 * Default ruleset matching TalentSense's standard Jacques curve categorization.
 * Clients can override this by storing a custom ruleset in the database.
 */
export const DEFAULT_POTENTIAL_RULESET = {
    rules: [
        {
            // Curve index >= 10 peaks at level 4B or higher — high trajectory
            when: { field: 'curva', op: '>=', value: 10 },
            then: 'HIGH',
        },
        {
            // Curve peaks at level 2, or at 3C (lowest level-3 sublevel) — low trajectory
            when: { or: [
                { field: 'peakLevel',     op: '==', value: '2'  },
                { field: 'nivelPotCurva', op: '==', value: '3C' },
            ]},
            then: 'LOW',
        },
        {
            // Curve peaks at level 3 (3B/3A), or at 4C (lowest level-4 sublevel) — mid trajectory
            when: { or: [
                { field: 'peakLevel',     op: '==', value: '3'  },
                { field: 'nivelPotCurva', op: '==', value: '4C' },
            ]},
            then: 'MEDIUM',
        },
    ],
    default: 'HIGH',
};
